import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth, isMfaRequiredError } from '../../contexts/AuthContext';
import { Eye, EyeOff, AlertCircle, CheckCircle2, ShieldCheck } from 'lucide-react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { ApiError } from '../../lib/api';
import { AuthPageLayout } from '../../components/auth/AuthPageLayout';
import MfaChallenge from '../../components/auth/MfaChallenge';
import { AUTH_REMEMBERED_EMAIL_KEY } from '../../utils/authHeaders';

const SignInPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // MFA challenge state: set when the server asks for a second factor.
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaEnrollmentRequired, setMfaEnrollmentRequired] = useState(false);

  const { login, completeMfaChallenge } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const resetSuccess = useMemo(() => searchParams.get('reset') === 'success', [searchParams]);
  
  useEffect(() => {
    const rememberedEmail = localStorage.getItem(AUTH_REMEMBERED_EMAIL_KEY);
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const normalizedEmail = email.trim().toLowerCase();
      await login(normalizedEmail, password, rememberMe);
      if (rememberMe) {
        localStorage.setItem(AUTH_REMEMBERED_EMAIL_KEY, normalizedEmail);
      } else {
        localStorage.removeItem(AUTH_REMEMBERED_EMAIL_KEY);
      }
      navigate(resolveTarget(), { replace: true });
    } catch (err: unknown) {
      // Second factor required: switch to the challenge screen (not an error).
      if (isMfaRequiredError(err)) {
        if (rememberMe) {
          localStorage.setItem(AUTH_REMEMBERED_EMAIL_KEY, email.trim().toLowerCase());
        }
        setMfaToken(err.mfaToken);
        setMfaEnrollmentRequired(err.enrollmentRequired);
        setError('');
        setIsLoading(false);
        return;
      }
      if (err instanceof ApiError && err.status === 401) {
        setError('Incorrect email or password.');
      } else if (err instanceof ApiError && typeof err.details?.detail === 'string') {
        setError(err.details.detail);
      } else if (err instanceof Error) {
        setError(err.message || 'Unable to sign in right now. Please try again.');
      } else {
        setError('Unable to sign in right now. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Resolve the post-login redirect target (saved return path, else role-based).
  const resolveTarget = (): string => {
    const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
    const safeFrom =
      from &&
      from.startsWith('/') &&
      !from.startsWith('//') &&
      !['/login', '/register', '/forgot-password', '/reset-password'].includes(from)
        ? from
        : undefined;
    const target = safeFrom || (window as any).__authLoginTarget || '/dashboard';
    delete (window as any).__authLoginTarget;
    return target;
  };

  // Second-factor challenge screen (shown after a correct password when MFA is on).
  if (mfaToken) {
    return (
      <MfaChallenge
        mfaToken={mfaToken}
        enrollmentRequired={mfaEnrollmentRequired}
        onSubmit={async (code: string) => {
          const target = await completeMfaChallenge(mfaToken, code);
          navigate(target, { replace: true });
        }}
        onCancel={() => {
          setMfaToken(null);
          setMfaEnrollmentRequired(false);
          setPassword('');
        }}
      />
    );
  }

  return (
    <AuthPageLayout
      footer={
        <div>
          <p className="text-slate-500">
            New to Cosellus?{' '}
            <Link to="/register" className="font-medium text-[#1f4778] hover:text-[#14315a] hover:underline">
              Create your account
            </Link>
          </p>
        </div>
      }
    >
      <div className="mb-6 space-y-2">
        <p className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
          <ShieldCheck className="h-3.5 w-3.5" /> Secure sign in
        </p>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Welcome back</h1>
        <p className="text-sm text-slate-500">Sign in to continue to your organization workspace.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {resetSuccess && (
          <div className="flex items-center gap-2.5 rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700 ring-1 ring-emerald-200">
            <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
            Password updated. Sign in with your new password.
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2.5 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 ring-1 ring-red-200">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <div>
          <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-slate-700">
            Email address
          </label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="block w-full rounded-lg border border-slate-400 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm transition focus:border-[#2fae2c] focus:outline-none focus:ring-2 focus:ring-[#40b93c]/30 placeholder:text-slate-400"
            placeholder="you@company.com"
          />
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between gap-3">
            <label htmlFor="password" className="text-sm font-medium text-slate-700">
              Password
            </label>
            <Link to="/forgot-password" className="text-xs font-medium text-[#1f4778] hover:underline">
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="block w-full rounded-lg border border-slate-400 bg-white px-3.5 py-2.5 pr-10 text-sm text-slate-900 shadow-sm transition focus:border-[#2fae2c] focus:outline-none focus:ring-2 focus:ring-[#40b93c]/30 placeholder:text-slate-400"
              placeholder="Enter your password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-slate-600">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="h-4 w-4 rounded border-slate-400 text-[#2fae2c] focus:ring-[#40b93c]/40"
          />
          Remember me
        </label>

        <button
          type="submit"
          disabled={isLoading}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#40b93c] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#2f962d] hover:shadow-[0_8px_20px_rgba(64,185,60,0.35)] focus:outline-none focus:ring-2 focus:ring-[#40b93c] focus:ring-offset-2 disabled:translate-y-0 disabled:opacity-50"
        >
          {isLoading ? <LoadingSpinner size="sm" /> : 'Sign in'}
        </button>
      </form>
    </AuthPageLayout>
  );
};

export default SignInPage;
